import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from '@mui/material';
import React from 'react';

export interface ToggleOption {
  value: string;
  label: string;
  ariaLabel?: string;
}

interface CustomToggleButtonGroupProps
  extends Omit<ToggleButtonGroupProps, 'onChange'> {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
}

const CustomToggleButtonGroup: React.FC<CustomToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
  size = 'small',
  exclusive = true,
  sx,
  ...otherProps
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null,
  ) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  const defaultSx = {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    padding: '2px',
    display: 'inline-flex',
    gap: '2px',
    '& .MuiToggleButtonGroup-grouped': {
      border: 'none',
      borderRadius: '6px',
      margin: 0,
      '&:not(:first-of-type)': {
        marginLeft: 0,
        borderLeft: 'none',
      },
    },
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 400,
      fontSize: '0.75rem',
      letterSpacing: '0.01em',
      padding: '5px 14px',
      minWidth: 'auto',
      color: 'rgba(0, 0, 0, 0.65)',
      border: 'none',
      borderRadius: '6px',
      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        color: 'rgba(0, 0, 0, 0.85)',
      },
      '&.Mui-selected': {
        backgroundColor: '#FFFFFF',
        color: '#0F23DF',
        fontWeight: 500,
        boxShadow:
          '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
        '&:hover': {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)',
        },
      },
      '&.Mui-disabled': {
        color: 'rgba(0, 0, 0, 0.26)',
      },
    },
    ...sx,
  };

  return (
    <ToggleButtonGroup
      value={value}
      exclusive={exclusive}
      onChange={handleChange}
      size={size}
      sx={defaultSx}
      {...otherProps}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.ariaLabel || option.value}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default CustomToggleButtonGroup;
